// src/products/capRaise/pages/InvestorCrmPage.js
// Complete Investor CRM system with UserInvites integration

import React, { useState, useEffect, useCallback } from 'react';
import { 
  Users, 
  Plus, 
  Mail, 
  Search,
  Send,
  AlertCircle,
  TrendingUp
} from 'lucide-react';
import capRaiseApi from '../services/capRaiseApi';
import { userInvitesApi } from '../../userInvites/api/userInvitesApi';
import AddInvestorModal from '../components/AddInvestorModal';

const InvestorCrmPage = ({ selectedCompany, activeRound, user, addMessage }) => {
  const [activeTab, setActiveTab] = useState('manage-investors');
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  
  // State for lead management
  const [sentLeads, setSentLeads] = useState([]);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [emailStats, setEmailStats] = useState({
    sent: 0,
    opened: 0,
    replied: 0
  });

  // Load investors from database
  const loadInvestors = useCallback(async () => {
    if (!activeRound) return;
    
    try {
      const { data, error } = await capRaiseApi.getInvestors(activeRound.id);
      if (error) throw error;
      setSentLeads(data || []);
    } catch (error) {
      console.error('Error loading investors:', error);
      addMessage?.('Error loading leads', 'error');
    }
  }, [activeRound, addMessage]);

  // Load email statistics
  const loadEmailStats = useCallback(async () => {
    if (!activeRound) return;
    
    try {
      const result = await userInvitesApi.getInvitesByRound(activeRound.id);
      
      if (result.status === 'success') {
        const invites = result.data || [];
        
        const stats = {
          sent: invites?.filter(i => i.invite_status !== 'pending').length || 0,
          opened: invites?.filter(i => i.invite_status === 'accepted').length || 0,
          replied: invites?.filter(i => i.invite_status === 'accepted').length || 0
        };
        
        setEmailStats(stats);
      } else {
        console.error('Error loading email stats:', result.message);
        setEmailStats({ sent: 0, opened: 0, replied: 0 });
      }
    } catch (error) {
      console.error('Error loading email stats:', error);
      setEmailStats({ sent: 0, opened: 0, replied: 0 });
    }
  }, [activeRound]);

  // Load investors/leads on mount and when round changes
  useEffect(() => {
    if (activeRound) {
      loadInvestors();
      loadEmailStats();
    }
  }, [activeRound, loadInvestors, loadEmailStats]);

  // Handle saving investors from modal
  const handleSaveInvestors = async (validLeads) => {
    if (!selectedCompany) {
      addMessage?.('Please select a company first', 'error');
      return;
    }
    
    if (!activeRound) {
      addMessage?.('Please create or select a fundraising round first', 'error');
      return;
    }

    setLoading(true);
    try {
      const savedLeads = [];
      for (const lead of validLeads) {
        const investorData = {
          round_id: activeRound.id,
          name: lead.name.trim(),
          email: lead.email.trim(),
          company: lead.company.trim(),
          status: 'prospect',
          investor_type: 'angel'
        };

        const result = await capRaiseApi.createInvestor(investorData);
        
        if (result.data) {
          savedLeads.push(result.data);
        } else if (result.error) {
          console.error('Error creating investor:', result.error);
          addMessage?.(`Error saving ${lead.name}: ${result.error.message || 'Unknown error'}`, 'error');
        }
      }

      if (savedLeads.length > 0) {
        addMessage?.(`Successfully added ${savedLeads.length} investors`, 'success');
        loadInvestors();
        setShowAddModal(false);
      }
    } catch (error) {
      console.error('Error saving investors:', error);
      addMessage?.('Error saving investors', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Toggle lead selection
  const toggleLeadSelection = (leadId) => {
    setSelectedLeads(prev => 
      prev.includes(leadId) 
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };

  // Select all filtered leads
  const selectAllFilteredLeads = () => {
    const filteredIds = filteredSentLeads.map(lead => lead.id);
    setSelectedLeads(prev => {
      const newSelection = [...new Set([...prev, ...filteredIds])];
      return newSelection;
    });
  };

  // Update lead status
  const updateLeadStatus = async (leadId, newStatus) => {
    try {
      const result = await capRaiseApi.updateInvestor(leadId, { status: newStatus });
      if (result.error) {
        addMessage?.(`Error updating status: ${result.error.message}`, 'error');
      } else {
        addMessage?.('Status updated successfully', 'success');
        loadInvestors();
      }
    } catch (error) {
      console.error('Error updating status:', error);
      addMessage?.('Error updating status', 'error');
    }
  };

  // Send invites using UserInvites system
  const handleSendInvites = async () => {
    if (selectedLeads.length === 0) {
      addMessage?.('Please select at least one lead', 'error');
      return;
    }

    setLoading(true);
    try {
      const leadsToInvite = sentLeads.filter(lead => selectedLeads.includes(lead.id));
      
      // FIXED: Use correct database field names
      const invitePromises = leadsToInvite.map(lead => 
        userInvitesApi.createInvite({
          email: lead.email,
          inviteforproduct: 'capraise',  // FIXED: was 'product'
          role: 'viewer',
          roundid: activeRound.id,  // FIXED: lowercase 'roundid'
          invitedby: user.id,
          customMessage: `You have been invited to view ${selectedCompany?.company_name}'s ${activeRound.round_name} fundraising round.`
        })
      );

      const results = await Promise.all(invitePromises);
      
      const successCount = results.filter(r => r.status === 'success').length;
      const failCount = results.length - successCount;

      if (successCount > 0) {
        addMessage?.(`Successfully sent ${successCount} invite(s)`, 'success');
        
        // Update lead status to 'contacted'
        for (const lead of leadsToInvite) {
          await capRaiseApi.updateInvestor(lead.id, { status: 'contacted' });
        }
      }
      
      if (failCount > 0) {
        addMessage?.(`Failed to send ${failCount} invite(s)`, 'error');
      }

      setSelectedLeads([]);
      loadInvestors();
      loadEmailStats();
    } catch (error) {
      console.error('Error sending invites:', error);
      addMessage?.('Error sending invites', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Filter sent leads
  const filteredSentLeads = sentLeads.filter(lead => {
    const matchesSearch = !searchTerm || 
      lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.company.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || lead.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Get status badge
  const getStatusBadge = (status) => {
    const statusMap = {
      prospect: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Not Invited' },
      contacted: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Invited' },
      interested: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Accepted' },
      committed: { bg: 'bg-green-100', text: 'text-green-800', label: 'Committed' }
    };
    
    const config = statusMap[status] || statusMap.prospect;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  // Render Leads Sent Tab
  const renderLeadsSentTab = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white p-4 rounded-lg border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Leads</p>
              <p className="text-2xl font-bold text-gray-900">{sentLeads.length}</p>
            </div>
            <Users className="w-8 h-8 text-blue-600" />
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Invites Sent</p>
              <p className="text-2xl font-bold text-gray-900">{emailStats.sent}</p>
            </div>
            <Mail className="w-8 h-8 text-green-600" />
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Responses</p>
              <p className="text-2xl font-bold text-gray-900">{emailStats.replied}</p>
            </div>
            <TrendingUp className="w-8 h-8 text-purple-600" />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border">
        <div className="p-4 border-b flex items-center justify-between">
          <div className="flex items-center space-x-4 flex-1">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Search leads..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Status</option>
              <option value="prospect">Not Invited</option>
              <option value="contacted">Invited</option>
              <option value="interested">Accepted</option>
              <option value="committed">Committed</option>
            </select>
          </div>
          <button
            onClick={selectAllFilteredLeads}
            className="ml-4 px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-md"
          >
            Select All
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  <input
                    type="checkbox"
                    checked={selectedLeads.length === filteredSentLeads.length && filteredSentLeads.length > 0}
                    onChange={selectAllFilteredLeads}
                    className="rounded border-gray-300"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredSentLeads.map((lead) => (
                <tr key={lead.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <input
                      type="checkbox"
                      checked={selectedLeads.includes(lead.id)}
                      onChange={() => toggleLeadSelection(lead.id)}
                      className="rounded border-gray-300"
                    />
                  </td>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">{lead.name}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{lead.email}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{lead.company}</td>
                  <td className="px-6 py-4">{getStatusBadge(lead.status)}</td>
                  <td className="px-6 py-4 text-sm">
                    <select
                      value={lead.status}
                      onChange={(e) => updateLeadStatus(lead.id, e.target.value)}
                      className="text-sm border-gray-300 rounded-md"
                    >
                      <option value="prospect">Not Invited</option>
                      <option value="contacted">Invited</option>
                      <option value="interested">Accepted</option>
                      <option value="committed">Committed</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredSentLeads.length === 0 && (
          <div className="text-center py-12">
            <Users className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No leads found</h3>
            <p className="mt-1 text-sm text-gray-500">Get started by adding your first lead.</p>
          </div>
        )}
      </div>
    </div>
  );

  // Render Send Invites Tab
  const renderSendInvitesTab = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-lg border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Send Secure Invitations</h3>
        <p className="text-sm text-gray-600 mb-6">
          Send secure magic link invitations to selected investors. They will receive an email with instant access to your fundraising round.
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Selected Investors: {selectedLeads.length}
            </label>
            {selectedLeads.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                <p className="text-sm text-blue-800">
                  {selectedLeads.length} investor(s) selected from the "Manage Investors" tab
                </p>
              </div>
            )}
          </div>

          <button
            onClick={handleSendInvites}
            disabled={loading || selectedLeads.length === 0}
            className="w-full py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center"
          >
            <Send className="w-5 h-5 mr-2" />
            {loading ? 'Sending...' : `Send to ${selectedLeads.length} Investor${selectedLeads.length !== 1 ? 's' : ''}`}
          </button>
        </div>

        {selectedLeads.length === 0 && (
          <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-yellow-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">No investors selected</h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>Please go to the "Manage Investors" tab and select the investors you want to invite.</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const tabs = [
    { id: 'manage-investors', label: 'Manage Investors', icon: Users },
    { id: 'send-invites', label: 'Send Invites', icon: Mail }
  ];

  if (!selectedCompany) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Users className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Loading...</h3>
          <p className="mt-1 text-sm text-gray-500">
            Loading company information...
          </p>
          <div className="mt-4 text-xs text-gray-400">
            Debug: selectedCompany = {JSON.stringify(selectedCompany)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Investor Management</h1>
          <p className="text-gray-600">Add investors first, then send secure invitations</p>
        </div>
        <div className="flex items-center space-x-4">
          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Investors
          </button>
          <span className="text-sm text-gray-500">
            Company: <span className="font-medium">{selectedCompany?.company_name}</span>
          </span>
          {activeRound && (
            <span className="text-sm text-gray-500">
              Round: <span className="font-medium">{activeRound.round_name}</span>
            </span>
          )}
        </div>
      </div>

      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="w-4 h-4 mr-2" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {activeTab === 'manage-investors' && renderLeadsSentTab()}
      {activeTab === 'send-invites' && renderSendInvitesTab()}

      {/* Add Investor Modal */}
      <AddInvestorModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSave={handleSaveInvestors}
        loading={loading}
      />
    </div>
  );
};

export default InvestorCrmPage;